import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
} from "react-router-dom";

import axiosInstance from "./utils/axios";

import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

import NotFound from "./pages/NotFound";


import ProfileSettings from './pages/ProfileSettings';
import SecuritySettings from './pages/SecuritySettings';
import Preferences from './pages/Preferences';
import HelpSupport from './pages/HelpSupport';

import LoginPage from "./pages/login";
import ForgotPassword from "./pages/forgot-password";
import ResetPassword from "./pages/reset-password";

import AssetAnalyticsDashboard from './features/asset-analytics-dashboard';
import QuarterlyAuditWorkflow from './features/quarterly-audit-workflow';
import AssetDetailManagement from './features/asset-detail-management';
import SystemConfigurationPanel from './features/system-configuration-panel';
import AssetManagementDashboard from './features/asset-management-dashboard';
import UserManagementConsole from './features/user-management-console';
import WarrantyManagementCenter from './features/warranty-management-center';
import SoftwareLicences from "./features/software-licences";
import ContractManagement from "./features/contract-management";
import VendorManagement from "./features/vendor-management";
import ProcurementManagement from "./features/procurement-management";
import PeopleOffboarding from "./features/people-offboarding";
import ServiceDesk from "./features/service-desk";
import AssetLifecycle from "./features/asset-lifecycle";  
import Offboarding from "./features/offboarding";
import DepartmentManagement from "./features/department-management";
import ContractCategories from "./features/contract-categories";
import VendorCategories from "./features/vendor-categories";
import VendorTypes from "./features/vendor-types";
import ContractTypes from "./features/contract-types";
import ProcurementCategories from "./features/procurement-categories";
// =====================================
// PROTECTED ROUTE
// =====================================

const ProtectedRoute = ({ children }) => {
  const token = sessionStorage.getItem("token");
  const storedUser = sessionStorage.getItem("user");

  // Immediate check: if user is stored as suspended, clear storage and log out
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      if (
        parsed?.status &&
        ['suspended', 'suspend'].includes(String(parsed.status).toLowerCase())
      ) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        return <Navigate to="/login?suspended=1" replace />;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Periodic and on-focus session verification: if account is suspended in the background, auto log out
  useEffect(() => {
    const verifySession = async () => {
      try {
        const response = await axiosInstance.get("/me");
        if (response?.data?.user?.status) {
          const status = String(response.data.user.status).toLowerCase();
          if (['suspended', 'suspend'].includes(status)) {
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("user");
            window.location.href = "/login?suspended=1";
          }
        }
      } catch (err) {
        // Intercepted and handled by axios response interceptor
      }
    };

    verifySession();

    const handleFocus = () => {
      verifySession();
    };
    window.addEventListener("focus", handleFocus);

    // Periodic check every 15 seconds
    const interval = setInterval(verifySession, 15000);

    return () => {
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, []);

  return children;
};


// =====================================
// PUBLIC ROUTE
// =====================================

const PublicRoute = ({ children }) => {

  const token = sessionStorage.getItem("token");

  if (token) {

    return <Navigate to="/" replace />;

  }

  return children;
};


// =====================================
// ROUTES
// =====================================

const Routes = () => {

  return (

    <BrowserRouter>

      <ErrorBoundary>

        <ScrollToTop />

        <RouterRoutes>

          {/* LOGIN */}

          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
        {/* FORGOT PASSWORD */}

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          {/* DASHBOARD */}

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AssetAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/software"
            element={
              <ProtectedRoute>
                <Navigate to="/asset-management-dashboard?tab=software" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract-management"
            element={
              <ProtectedRoute>
                <ContractManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendor-management"
            element={
              <ProtectedRoute>
                <VendorManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/procurement-management"
            element={
              <ProtectedRoute>
                <ProcurementManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/offboarding"
            element={
              <ProtectedRoute>
                <Navigate to="/asset-management-dashboard?tab=recovery" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/people-offboarding"
            element={
              <ProtectedRoute>
                <Navigate to="/asset-management-dashboard?tab=recovery" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-desk"
            element={
              <ProtectedRoute>
                <ServiceDesk />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-requests"
            element={
              <ProtectedRoute>
                <ServiceDesk />
              </ProtectedRoute>
            }
          />
          <Route
            path="/department-management"
            element={
              <ProtectedRoute>
                <DepartmentManagement />
              </ProtectedRoute>
            }
          />  
          <Route
            path="/asset-lifecycle"
            element={
              <ProtectedRoute>
                <AssetLifecycle />
              </ProtectedRoute>
            }
          />

          {/* CONTRACT CATEGORIES */}

      <Route
        path="/contract-categories"
        element={
          <ProtectedRoute>
            <ContractCategories />
          </ProtectedRoute>
        }
      />

      {/* VENDOR CATEGORIES */}

      <Route
        path="/vendor-categories"
        element={
          <ProtectedRoute>
            <VendorCategories />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor-types"
        element={
          <ProtectedRoute>
            <VendorTypes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contract-types"
        element={
          <ProtectedRoute>
            <ContractTypes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/procurement-categories"
        element={
          <ProtectedRoute>
            <ProcurementCategories />
          </ProtectedRoute>
        }
      />
          <Route
            path="/asset-analytics-dashboard"
            element={
              <ProtectedRoute>
                <AssetAnalyticsDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/quarterly-audit-workflow"
            element={
              <ProtectedRoute>
                <QuarterlyAuditWorkflow />
              </ProtectedRoute>
            }
          />

          <Route
            path="/asset-detail-management"
            element={
              <ProtectedRoute>
                <AssetDetailManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/system-configuration-panel"
            element={
              <ProtectedRoute>
                <SystemConfigurationPanel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/asset-management-dashboard"
            element={
              <ProtectedRoute>
                <AssetManagementDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user-management-console"
            element={
              <ProtectedRoute>
                <UserManagementConsole />
              </ProtectedRoute>
            }
          />

          <Route
            path="/warranty-management-center"
            element={
              <ProtectedRoute>
                <WarrantyManagementCenter />
              </ProtectedRoute>
            }
          />

          {/* SETTINGS */}

          <Route
            path="/profile-settings"
            element={
              <ProtectedRoute>
                <ProfileSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/security-settings"
            element={
              <ProtectedRoute>
                <SecuritySettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/preferences"
            element={
              <ProtectedRoute>
                <Preferences />
              </ProtectedRoute>
            }
          />

          <Route
            path="/help-support"
            element={
              <ProtectedRoute>
                <HelpSupport />
              </ProtectedRoute>
            }
          />

          {/* 404 */}

          <Route path="*" element={<NotFound />} />

        </RouterRoutes>

      </ErrorBoundary>

    </BrowserRouter>

  );
};

export default Routes;